import {
	LocatedVelavuDevice,
	LocationResource,
	NormalizedDeviceHardware,
	normalizeDeviceHardware,
	VelavuDevice,
	VelavuSite,
} from "velavu-js-api";
import { viewport as geoViewport } from "@mapbox/geo-viewport";
import mapboxgl from "mapbox-gl";

const MAPBOX_TILE_SIZE = 512;

export interface ViewportPadding {
	left?: number;
	right?: number;
	top?: number;
	bottom?: number;
}

export interface DevicePositionAnimation {
	startCoords: [number, number];
	startDate: number;
	endCoords: [number, number];
	endDate: number;
}

/**
 * DeviceMarker is a custom type that represents the
 * consolidated state of a renderable asset / device on a map
 */
export default interface DeviceMarker {
	deviceID: string;
	assetID?: string;
	name?: string;
	online?: boolean;
	hardware: string | undefined;
	location: LocationResource;
	anim?: DevicePositionAnimation;
	inSite: boolean;
	displayZoomThreshold: number | undefined;
	priority: number;
}

export function deviceToDeviceMarker(
	device: LocatedVelavuDevice,
	mapDimensions: [number, number],
	viewportPadding?: ViewportPadding,
	site?: VelavuSite,
): DeviceMarker;
export function deviceToDeviceMarker(
	device: VelavuDevice,
	mapDimensions: [number, number],
	viewportPadding?: ViewportPadding,
	site?: VelavuSite,
): DeviceMarker | undefined;
export function deviceToDeviceMarker(
	device: VelavuDevice,
	mapDimensions: [number, number],
	viewportPadding?: ViewportPadding,
	site?: VelavuSite,
): DeviceMarker | undefined {
	//Device markers must have a location
	if (device.location === undefined) {
		return undefined;
	}

	let priority: number;
	switch (normalizeDeviceHardware(device.hardware)) {
		case NormalizedDeviceHardware.Meridian:
			priority = 3;
			break;
		case NormalizedDeviceHardware.Argo:
			priority = 2;
			break;
		case NormalizedDeviceHardware.Juno:
			priority = 1;
			break;
		default:
			priority = 0;
			break;
	}

	return {
		deviceID: device.id,
		assetID: device.asset?.id,
		name: device.asset?.name,
		online: device.online,
		hardware: device.hardware,
		location: device.location,
		inSite: device.site_id !== undefined,
		displayZoomThreshold:
			device.site_id !== undefined
				? resolveSiteZoomLevel(site, mapDimensions, viewportPadding)
				: undefined,
		priority,
	};
}

/// Resolves the coordinates of a device marker at a given time
export function resolveDeviceMarkerCoordinates(
	deviceMarker: DeviceMarker,
	time: number,
): [number, number] {
	//If the device has no animation, return the current coordinates
	if (deviceMarker.anim === undefined) {
		return deviceMarker.location.coordinates;
	}

	//Interpolate the coordinates based on the animation
	const timeDelta = time - deviceMarker.anim.startDate;
	const timeTotal = deviceMarker.anim.endDate - deviceMarker.anim.startDate;
	const progress = Math.min(1, Math.max(0, timeDelta / timeTotal));

	const xDelta =
		deviceMarker.anim.endCoords[0] - deviceMarker.anim.startCoords[0];
	const yDelta =
		deviceMarker.anim.endCoords[1] - deviceMarker.anim.startCoords[1];

	return [
		deviceMarker.anim.startCoords[0] + xDelta * progress,
		deviceMarker.anim.startCoords[1] + yDelta * progress,
	];
}

export interface SiteBoundaryViewport {
	bounds: [number, number, number, number];
	zoom: number;
	center: [number, number];
}

/// The offset to apply to the computed zoom threshold for a site
const SITE_ZOOM_THRESHOLD_OFFSET = -1.5;

// Resolves the appropriate coordinates and zoom level
// for inspecting a site
export function resolveSiteBoundaryViewport(
	site: VelavuSite | undefined,
	mapDimensions: [number, number],
	padding: ViewportPadding = {},
): SiteBoundaryViewport | undefined {
	//Make sure the site has floors we can check
	if (site === undefined || site.floors === undefined) {
		return undefined;
	}

	//Collect the coordinates of all floor points
	const coordinateArray = site.floors.flatMap((floor) => {
		return floor.geometry.walls.features.flatMap((feature) => {
			return feature.geometry.coordinates as [number, number][];
		});
	});
	if (coordinateArray.length === 0) {
		return undefined;
	}

	//Resolve the min and max coords of the site floors
	const bounds = new mapboxgl.LngLatBounds();
	for (const coords of coordinateArray) {
		bounds.extend(coords);
	}
	const boundsArray: [number, number, number, number] = [
		bounds.getWest(),
		bounds.getSouth(),
		bounds.getEast(),
		bounds.getNorth(),
	];
	const leftPadding = padding.left ?? 0;
	const rightPadding = padding.right ?? 0;
	const topPadding = padding.top ?? 0;
	const bottomPadding = padding.bottom ?? 0;
	const usableMapDimensions: [number, number] = [
		Math.max(1, mapDimensions[0] - leftPadding - rightPadding),
		Math.max(1, mapDimensions[1] - topPadding - bottomPadding),
	];

	const viewport = geoViewport(
		boundsArray,
		usableMapDimensions,
		undefined,
		undefined,
		MAPBOX_TILE_SIZE,
		true,
	);
	const viewportZoom = viewport.zoom + SITE_ZOOM_THRESHOLD_OFFSET;
	const horizontalOffsetPixels = (leftPadding - rightPadding) / 2;
	const lngOffset =
		(horizontalOffsetPixels * 360) /
		(MAPBOX_TILE_SIZE * Math.pow(2, viewportZoom));

	return {
		bounds: boundsArray,
		zoom: viewportZoom,
		center: [viewport.center[0] - lngOffset, viewport.center[1]],
	};
}

/// The default zoom threshold to use for a site if it cannot be resolved
const FALLBACK_SITE_ZOOM_THRESHOLD = 17;

// Resolves the appropriate zoom level for a given site
// at which to reveal its assets
export function resolveSiteZoomLevel(
	site: VelavuSite | undefined,
	mapDimensions: [number, number],
	padding: ViewportPadding = {},
): number {
	const viewport = resolveSiteBoundaryViewport(site, mapDimensions, padding);
	if (viewport === undefined) {
		return FALLBACK_SITE_ZOOM_THRESHOLD;
	}
	return viewport.zoom;
}
